import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { App as AntApp, Button, Input, InputNumber } from 'antd';
import SheetDate from '../components/SheetDate';
import dayjs from 'dayjs';
import AppBar from '../components/AppBar';
import DocViewer from '../components/DocViewer';
import FileViewerLazy from '../components/FileViewerLazy';
import SheetSelect, { OptionSheet } from '../components/SheetSelect';
import { InventoryEditor } from '../components/AgreementExtras';
import { AsyncButton, Chip, DOC_CATEGORY_TONE, I, Skeletons } from '../components/kit';
import { RestoredDraftBanner } from '../components/AutosaveStatus';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import useFormAutosave from '../hooks/useFormAutosave';
import useSavedCopy from '../hooks/useSavedCopy';
import * as documentsApi from '../api/documents';
import { fileFormat, isPdfFile } from '../lib/format';
import { matchOption, selectOptions, fieldShown, revealHanded } from '../lib/docFieldOptions';
import {
  AGREEMENTS_NAME, agreementSub, agreementTypeFor, docSlug, fillFromAgreement, fillFromHint, replacesOwnInventory,
} from '../lib/louFromAgreement';
import { listedCount } from '../lib/inventoryList';
import useAgreementOptions from '../hooks/useAgreementOptions';

/**
 * Shorten from the MIDDLE, on word boundaries, keeping both ends.
 *
 * These letters are told apart by their first or last word — "Authorisation to
 * Secure a Property for Purchase" vs "…for Rent", "Vendor's Letter of
 * Undertaking" vs "Landlord's…". A plain tail ellipsis hides exactly the word
 * that identifies the document, which is the whole reason the collapsed bar
 * shows a title at all.
 */
function shortenMiddle(text, max = 38) {
  const s = String(text || '');
  if (s.length <= max) return s;
  const words = s.split(' ');
  const head = [];
  const tail = [];
  let headLen = 0;
  let tailLen = 0;
  while (words.length) {
    if (headLen <= tailLen) {
      const w = words.shift();
      if (headLen + w.length + 1 > max / 2) { words.unshift(w); break; }
      head.push(w); headLen += w.length + 1;
    } else {
      const w = words.pop();
      if (tailLen + w.length + 1 > max / 2) { words.push(w); break; }
      tail.unshift(w); tailLen += w.length + 1;
    }
  }
  if (!head.length || !tail.length) return `${s.slice(0, max - 1)}…`;
  return `${head.join(' ')} … ${tail.join(' ')}`;
}

// Module-scope on purpose: defining this inside the page component would give it
// a new identity every render, making React remount the subtree and drop input
// focus on each keystroke.
const Section = ({ title, sub, children }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16 }}>
    <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
    {sub && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{sub}</div>}
    <div style={{ marginTop: 10 }}>{children}</div>
  </div>
);

// A value that counts as filled in — what a signer's `showIfFilled` waits for.
const filled = (v) => v !== undefined && v !== null && String(v).trim() !== '';

// The signers offered right now: a second party's pad (`showIfFilled`) appears only
// once their name is in.
const shownSigners = (signing, values) => (signing?.signers || [])
  .filter((s) => !s.showIfFilled || filled(values[s.showIfFilled]));

// The signatures that go to the server — never one for a signer who is not offered.
const sigsToSend = (sigs, signing, values) => {
  const keys = new Set(shownSigners(signing, values).map((s) => s.key));
  if (signing?.witness) keys.add('witness');
  return Object.fromEntries(Object.entries(sigs).filter(([k]) => keys.has(k)));
};

/**
 * Document workspace (CID Forms → Preview): the signing-page-style view for one
 * library document. Agent can optionally fill the document's fields, capture
 * on-device signatures (incl. witness where the paper form has one), preview the
 * result in Readable/Original modes (DocViewer), and print/save it. Each print is
 * logged for HQ tracking, and a numbered document is SAVED under its Ref No. with
 * the values it was printed with (useSavedCopy): "Printed by you" on the CID Forms
 * list opens it again, and printing it again keeps the number.
 *
 * A fillable document has no "download blank" copy: the stored PDF is a stale
 * duplicate of the template (hardcoded 6% SST, HQ's address in the LOU "TO:"
 * block) and the server refuses to serve it. Printing with nothing filled gives
 * the same blank form, with the right rate and the agent's own branch. Download
 * survives only for the download-only documents, which have no template.
 */
export default function DocumentWorkspacePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { message, modal } = AntApp.useApp();

  const [doc, setDoc] = useState(null);
  const [schema, setSchema] = useState(null); // { fields, signing } | null (no prefill)
  const [loaded, setLoaded] = useState(false);
  const [values, setValues] = useState({});
  const [sigs, setSigs] = useState({}); // slot key -> dataURL
  const [activePad, setActivePad] = useState(null); // slot key being signed
  const [witnessOpen, setWitnessOpen] = useState(false);
  const [viewerHtml, setViewerHtml] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null); // object URL for the blank PDF overlay
  // The saved copy being worked on (?issue=), or none for a new document.
  const saved = useSavedCopy();
  const [copyMissing, setCopyMissing] = useState(false);

  // Collapsing header: the bar reads "CID Forms" until the page title scrolls
  // out of view, then crossfades to the document's own title so you always know
  // which form you are in without a permanently tall header.
  const [collapsed, setCollapsed] = useState(false);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    // The first print just named the copy in the URL — what is on screen IS it.
    if (saved.justAdopted()) return undefined;
    let on = true;
    // Also runs when the agent switches between a saved copy and a new one on the
    // same document ("Start a new one"), so it starts from a clean sheet.
    setLoaded(false);
    setSigs({});
    setActivePad(null);
    setCopyMissing(false);
    (async () => {
      try {
        const list = await documentsApi.listDocuments();
        const d = (list.data || []).find((x) => x.id === id);
        let opened = null;
        try {
          opened = await saved.load();
        } catch {
          if (on) setCopyMissing(true);
        }
        if (!on) return;
        // A copy of a different document under this URL is not this page's to show.
        if (opened && opened.copy.document_id !== id) { setCopyMissing(true); opened = null; }
        setDoc(d || null);
        if (d?.prefill_available) {
          const s = await documentsApi.getPrefillSchema(d.id);
          if (!on) return;
          setSchema(s.data || null);
          const init = {};
          for (const f of s.data?.fields || []) if (f.value !== undefined) init[f.key] = f.value;
          // A saved copy comes back exactly as it was last printed.
          if (opened) Object.assign(init, opened.values);
          // Prefill handed over via router state (e.g. a Copilot card) — only
          // known field keys, numbers coerced for the number inputs.
          const handed = opened ? null : location.state?.prefill;
          if (handed && typeof handed === 'object') {
            const applied = new Set();
            for (const f of s.data?.fields || []) {
              const v = handed[f.key];
              if (v === undefined || v === null || v === '') continue;
              // Only scalars are handed over: the Inventory List is a whole object
              // the agent builds here, and String() would turn it into
              // "[object Object]".
              if (f.type === 'inventory' || typeof v === 'object') continue;
              if (f.type === 'money' || f.type === 'number') {
                const n = Number(v);
                if (Number.isFinite(n)) { init[f.key] = n; applied.add(f.key); }
              } else if (f.type === 'select' && f.options) {
                const o = matchOption(f.options, v);
                if (o !== undefined) { init[f.key] = o; applied.add(f.key); }
              } else {
                init[f.key] = String(v);
                applied.add(f.key);
              }
            }
            revealHanded(s.data?.fields, init, applied);
          }
          setValues(init);
        }
      } finally {
        if (on) setLoaded(true);
      }
    })();
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, saved.issueId]);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  // Watch the sentinel that sits under the page title. IntersectionObserver
  // rather than a scroll handler: no per-frame work while the agent types.
  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setCollapsed(!entry.isIntersecting),
      { root, threshold: 0 },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [loaded, doc]);

  // Every user edit goes through here (or calls noteChange itself), so it is also
  // where the device copy hears of it. The load and a handed-over prefill do not.
  const setV = (k, v) => {
    setValues((s) => ({ ...s, [k]: v }));
    // Emptying a second party's name takes their signature pad away (showIfFilled),
    // and the signature given there with it — it must not come back under a
    // different name typed in later.
    if (!filled(v)) {
      for (const sg of schema?.signing?.signers || []) {
        if (sg.showIfFilled === k) {
          setSigs((x) => { const n = { ...x }; delete n[sg.key]; return n; });
          setActivePad((a) => (a === sg.key ? null : a));
        }
      }
    }
    autosave.noteChange();
  };
  const putSig = (key, dataUrl) => {
    setSigs((s) => {
      const n = { ...s };
      if (dataUrl) n[key] = dataUrl; else delete n[key];
      return n;
    });
    autosave.noteChange();
  };

  /* Device copy, the owner's "A" (2026-10-02): until it is printed nothing here
     reaches the server, so the values are kept on this device a moment after each
     change and come back after a reload. Same key as the desktop DocumentWorkspace,
     so rotating an iPad across the 1100px switch brings them across. A saved copy
     keeps its own key (…:issue:<id>), so editing it never overwrites the new
     document being drafted on the same form, and a device copy older than the
     server's (printed since on another device) yields to it.

     Drawn signatures are NOT kept. A signature given on this device is the
     signer's assent to the sheet in front of them at that moment; bringing it
     back hours later onto a sheet that may have been edited since would put it on
     terms they never saw. The guard still counts them, so leaving with one
     captured asks first, and a restore says how many need signing again.

     Printing is saving (2026-10-14): a print the server kept under its Ref No.
     drops the device copy — the saved copy has the values now. A print it could
     not keep leaves the device copy as it was, and the paper has the values, so
     the guard stays quiet until the next edit either way. */
  const { agent } = useAuth() || {};
  const [loadedTick, setLoadedTick] = useState(0);
  useEffect(() => { if (loaded && schema) setLoadedTick((t) => t + 1); }, [loaded, schema]);
  // How many signatures the restored copy had and could not bring back.
  const [sigsToRedo, setSigsToRedo] = useState(0);
  const autosave = useFormAutosave({
    draftKey: schema ? (saved.issueId ? `document:${id}:issue:${saved.issueId}` : `document:${id}`) : null,
    userId: agent?.id,
    serverUpdatedAt: saved.copy?.updated_at,
    readValues: () => values,
    readMeta: () => ({ witnessOpen, signatures: Object.keys(sigs).length }),
    writeValues: (v, meta) => {
      setValues(v || {});
      setWitnessOpen(!!meta.witnessOpen);
      setSigsToRedo(meta.signatures || 0);
    },
    loadedTick,
  });
  // The change token at the last print; -1 = not printed on this visit.
  const [printedAt, setPrintedAt] = useState(-1);
  const guard = useUnsavedChanges(autosave.dirty && autosave.changeToken() !== printedAt, {
    what: 'this document',
    keptOnDevice: true,
    onDiscard: autosave.discardDevice,
  });

  const downloadBlank = async () => {
    const r = await documentsApi.downloadDocument(doc.id);
    // An iPhone app on the home screen drops a plain download; the share sheet
    // (Save to Files, Open in Word…) is the reliable way out, as in FileViewer.
    const ios = /iPhone|iPod|iPad/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const file = new File([r], doc.file_name || `${doc.title}.pdf`, { type: r.type || doc.mime_type || 'application/octet-stream' });
    if (ios && navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: file.name }); return; } catch (e) { if (e.name === 'AbortError') return; }
    }
    const url = URL.createObjectURL(r instanceof Blob ? r : new Blob([r]));
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.file_name || `${doc.title}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    message.success('Downloaded');
  };

  const viewBlankPdf = async () => {
    const r = await documentsApi.downloadDocument(doc.id);
    const blob = r instanceof Blob ? r : new Blob([r], { type: 'application/pdf' });
    setPdfUrl(URL.createObjectURL(blob));
  };

  const preview = async () => {
    const r = await documentsApi.renderPrefill(doc.id, values, sigsToSend(sigs, schema?.signing, values), false, saved.copy?.id); // preview never consumes a Ref No.
    if (!r.data?.html) throw new Error('Could not render the document');
    setViewerHtml(r.data.html);
  };

  const printDoc = async () => {
    // A saved copy reprints under its own number; a new document takes the next one.
    const wasNew = !saved.copy;
    const r = await documentsApi.renderPrefill(doc.id, values, sigsToSend(sigs, schema?.signing, values), true, saved.copy?.id);
    const html = r.data?.html;
    if (!html) { message.error('Could not render the document'); return; }
    // Recorded before the popup opens: the number is taken and the copy saved
    // either way, so a blocked popup must not make the retry take another number.
    const t = autosave.changeToken();
    setPrintedAt(t);
    if (r.data?.issue_id) autosave.markSaved(t); // still the pre-print key: drops that device copy
    const outcome = saved.recordPrint(r.data, { allowNav: guard.allowOnce });
    if (outcome === 'saved' && wasNew) message.success(`Saved as ${r.data.doc_ref} — open it again any time from Printed by you`);
    if (outcome === 'unsaved') message.warning(`Printed as ${r.data.doc_ref}, but this copy could not be saved — keep the printed one.`);
    const w = window.open('', '_blank');
    if (!w) { message.error('Popup blocked — please allow popups and try again'); return; }
    w.document.write(html);
    w.document.close();
    w.document.title = doc.title;
    const imgs = w.document.querySelectorAll('img');
    let n = 0;
    const fire = () => { w.focus(); w.print(); };
    if (!imgs.length) fire();
    else imgs.forEach((img) => {
      const done = () => { n += 1; if (n >= imgs.length) fire(); };
      if (img.complete) done(); else { img.onload = done; img.onerror = done; }
    });
  };

  /* "Fill from an OTP / OTR" — the LOUs and the Inventory Lists come out of an
     agreement, so the agent can take its details from one of their own instead of
     retyping them (lib/louFromAgreement holds the mapping). The values are MERGED:
     what the agreement leaves blank keeps what was typed here. */
  const slug = docSlug(doc);
  const agreementType = schema ? agreementTypeFor(doc) : null;
  const [pickOpen, setPickOpen] = useState(false);
  const picker = useAgreementOptions(agreementType, pickOpen);
  const fillFrom = async (agreementId) => {
    let got;
    try {
      got = await fillFromAgreement(slug, agreementId, (schema?.fields || []).map((f) => f.key));
    } catch (e) {
      message.error(e.message || 'Could not read that agreement');
      return;
    }
    const { form, patch } = got;
    const apply = (p) => {
      const n = Object.keys(p).length;
      if (!n) { message.warning(`${form.form_ref} has none of this document’s details filled in yet`); return; }
      setValues((s) => ({ ...s, ...p }));
      autosave.noteChange();
      message.success(`Filled ${n} field${n === 1 ? '' : 's'} from ${form.form_ref}`);
    };
    if (!replacesOwnInventory(values.inventory, patch)) { apply(patch); return; }
    const { inventory, ...rest } = patch;
    modal.confirm({
      title: 'Replace your Inventory List?',
      content: `${form.form_ref} carries its own list (${listedCount(inventory)} items). Replace the ${listedCount(values.inventory)} you have listed here with it?`,
      okText: 'Use the agreement’s list',
      cancelText: 'Keep mine',
      centered: true,
      onOk: () => apply(patch),
      onCancel: () => apply(rest),
    });
  };

  const fieldInput = (f) => {
    const v = values[f.key];
    // The agent's own list — the same editor the agreement's Inventory List uses.
    if (f.type === 'inventory') return <InventoryEditor inv={v} onChange={(next) => setV(f.key, next)} />;
    if (f.type === 'date') {
      // Stored as the DD/MM/YYYY string the document template prints verbatim.
      return (
        <SheetDate
          title={f.label}
          placeholder={`Pick ${String(f.label || 'a date').toLowerCase()}`}
          format="DD/MM/YYYY"
          value={v ? dayjs(v, 'DD/MM/YYYY') : null}
          onChange={(d) => setV(f.key, d ? d.format('DD/MM/YYYY') : undefined)}
        />
      );
    }
    if (f.type === 'money' || f.type === 'number') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          min={0}
          formatter={f.type === 'money' ? (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : undefined}
          parser={f.type === 'money' ? (s) => String(s).replace(/,/g, '') : undefined}
          value={v}
          onChange={(n) => setV(f.key, n ?? undefined)}
        />
      );
    }
    if (f.type === 'select') {
      return (
        <SheetSelect
          title={f.label}
          placeholder={`Select ${String(f.label || '').toLowerCase() || 'an option'}`}
          allowClear={f.clearable !== false}
          options={selectOptions(f.options)}
          value={v}
          onChange={(x) => setV(f.key, x)}
        />
      );
    }
    if (f.type === 'textarea') return <Input.TextArea rows={2} value={v} onChange={(e) => setV(f.key, e.target.value)} />;
    return <Input value={v} onChange={(e) => setV(f.key, e.target.value)} />;
  };

  if (!loaded) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar back title="Document" />
        <div style={{ padding: 18 }}><Skeletons n={4} height={90} /></div>
      </div>
    );
  }
  if (copyMissing) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar back title="Document" />
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13, lineHeight: 1.6 }}>
          This saved copy could not be found.
          <div><Button type="link" onClick={() => nav('/downloads?view=printed')}>Back to Printed by you</Button></div>
        </div>
      </div>
    );
  }
  if (!doc) {
    // A saved copy of a document HQ has since withdrawn: its number stays on the
    // paper already given out, but the letter can no longer be printed from here.
    const withdrawn = saved.copy;
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar back title="Document" />
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13, lineHeight: 1.6 }}>
          {withdrawn
            ? <>{withdrawn.title || 'This document'} ({withdrawn.doc_ref}) has been withdrawn by HQ, so it can no longer be printed.</>
            : 'Document not found.'}
          <div><Button type="link" onClick={() => nav('/downloads')}>Back to CID Forms</Button></div>
        </div>
      </div>
    );
  }

  const signing = schema?.signing || null;
  const shownFields = (schema?.fields || []).filter((f) => f.group !== 'witness' && fieldShown(f, values, schema.fields));
  const mainFields = shownFields.filter((f) => f.type !== 'inventory');
  const listFields = shownFields.filter((f) => f.type === 'inventory');
  const witnessFields = (schema?.fields || []).filter((f) => f.group === 'witness');
  const signers = shownSigners(signing, values);
  const sigCount = Object.keys(sigsToSend(sigs, signing, values)).length;
  const restoredNote = sigsToRedo
    ? `Signatures are not kept on the device — ${sigsToRedo === 1 ? 'the one captured needs' : `the ${sigsToRedo} captured need`} signing again.`
    : saved.copy
      ? 'Print it again to save these changes to this copy.'
      : 'Print it to save it — it then appears under Printed by you.';
  const copy = saved.copy;

  const sigSlot = (key, label) => (
    <div key={key} style={{ border: '1px solid var(--border)', borderRadius: 11, padding: '14px 15px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{label}</div>
        {sigs[key] ? (
          <>
            <img src={sigs[key]} alt="signature" style={{ height: 30, background: '#fff', border: '1px solid var(--border)', borderRadius: 6 }} />
            <Button size="small" onClick={() => putSig(key, null)}>Clear</Button>
          </>
        ) : (
          <Button size="small" type={activePad === key ? 'default' : 'primary'} ghost={activePad !== key} onClick={() => setActivePad(activePad === key ? null : key)}>
            {activePad === key ? 'Cancel' : 'Sign'}
          </Button>
        )}
      </div>
      {activePad === key && !sigs[key] && (
        <div style={{ marginTop: 10 }}>
          <SignPad onDone={(dataUrl) => { putSig(key, dataUrl); setActivePad(null); }} />
        </div>
      )}
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Slim bar; the title crossfades in once the heading below scrolls away. */}
      <AppBar
        back
        title={
          <span style={{ position: 'relative', display: 'block', height: 21 }}>
            <span style={{ position: 'absolute', inset: 0, opacity: collapsed ? 0 : 1, transition: 'opacity .18s ease' }}>
              CID Forms
            </span>
            <span
              style={{
                position: 'absolute',
                inset: 0,
                opacity: collapsed ? 1 : 0,
                transition: 'opacity .18s ease',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {shortenMiddle(doc.title)}
            </span>
          </span>
        }
      />

      <div ref={scrollRef} className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Page heading — the document's identity, free to wrap to any length. */}
        <div>
          <div style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.32 }}>{doc.title}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {doc.category && <Chip label={doc.category} tone={DOC_CATEGORY_TONE[doc.category] || 'brand'} />}
            {doc.requires_letterhead && <Chip label="Letterhead" tone="muted" />}
            {schema && <Chip label="Fillable" tone="ok" />}
          </div>
          {doc.description && (
            <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: 10 }}>{doc.description}</div>
          )}
        </div>
        <div ref={sentinelRef} style={{ height: 1, flex: 'none' }} />

        {copy && (
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 11, padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                Ref No. <span style={{ fontVariantNumeric: 'tabular-nums' }}>{copy.doc_ref}</span> <span style={{ fontWeight: 400, color: 'var(--muted)' }}>· saved copy</span>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: 3 }}>
                Printing again keeps this number.
                {copy.last_printed_at ? ` Printed ${copy.print_count || 1}× · last ${dayjs(copy.last_printed_at).format('D MMM YYYY, h:mm a')}.` : ''}
                {schema?.signing ? ' Signatures are not saved — sign again before printing.' : ''}
              </div>
            </div>
            <Button size="small" onClick={() => nav(`/downloads/${id}`)}>Start a new one</Button>
          </div>
        )}

        {schema ? (
          <>
            <RestoredDraftBanner autosave={autosave} notYet={restoredNote} />
            <Section title="Fill in details" sub="All optional — anything left empty prints as a blank line to complete by hand, so printing with nothing filled gives you a blank form.">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {agreementType && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, paddingBottom: 12, borderBottom: '1px dashed var(--border)' }}>
                    <Button data-demo="fill-from-agreement" block onClick={() => setPickOpen(true)}>Fill from an {agreementType}</Button>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>{fillFromHint(doc)}</div>
                  </div>
                )}
                {mainFields.map((f) => (
                  <div key={f.key}>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 3 }}>{f.label}</div>
                    {fieldInput(f)}
                  </div>
                ))}
              </div>
            </Section>

            {/* The list is the document's body, so it gets a section of its own
                rather than sitting at the bottom of a column of name fields. */}
            {listFields.map((f) => (
              <Section key={f.key} title="Inventory List" sub="The rooms and items are yours to build — add, rename or remove any.">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{fieldInput(f)}</div>
              </Section>
            ))}

            {signing ? (
              <Section title="Sign on this device" sub="Optional — capture signatures now, or print and sign on paper.">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {signers.map((s) => sigSlot(s.key, s.label))}
                  {signing.witness && !witnessOpen && (
                    <Button block onClick={() => setWitnessOpen(true)}>＋ Add witness</Button>
                  )}
                  {signing.witness && witnessOpen && (
                    <div style={{ border: '1px dashed var(--border)', borderRadius: 11, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600 }}>Witness <span style={{ fontWeight: 400, color: 'var(--muted)' }}>· defaults to you if left empty</span></div>
                      {witnessFields.map((f) => (
                        <div key={f.key}>
                          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 3 }}>{f.label}</div>
                          {fieldInput(f)}
                        </div>
                      ))}
                      {sigSlot('witness', 'Witness signature')}
                    </div>
                  )}
                </div>
              </Section>
            ) : (
              <div style={{ fontSize: 11.5, color: 'var(--muted)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 11, padding: '13px 14px' }}>
                For this document, use a booking form for full e-signing (email links with OTP verification and HQ approval).
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--muted)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 11, padding: '14px 15px' }}>
            This document has no fillable fields — {isPdfFile(doc) ? 'view or download the official PDF below.' : `download the official ${fileFormat(doc)} file below.`}
          </div>
        )}

        <div style={{ height: 120 }} />
      </div>

      {/* Bottom action bar — one evenly split row in both modes. It holds only
          the two steps of the actual flow (check it, then produce it); the
          "get an empty copy" action lives up in the body. */}
      <div style={{ flex: 'none', padding: '10px 16px calc(12px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--border)', display: 'flex', gap: 12 }}>
        {schema ? (
          <>
            <AsyncButton style={{ flex: 1, minWidth: 0 }} type="primary" onClick={preview} onError={(e) => message.error(e.message)}>
              Preview{sigCount ? ` · ${sigCount}` : ''}
            </AsyncButton>
            <AsyncButton style={{ flex: 1, minWidth: 0 }} onClick={printDoc}>Print / save PDF</AsyncButton>
          </>
        ) : (
          <>
            {/* Only a PDF opens in the viewer; a Word or Excel file is download-only. */}
            {isPdfFile(doc) && (
              <AsyncButton style={{ flex: 1, minWidth: 0 }} type="primary" onClick={viewBlankPdf}>View PDF</AsyncButton>
            )}
            <AsyncButton style={{ flex: 1, minWidth: 0 }} type={isPdfFile(doc) ? 'default' : 'primary'} onClick={downloadBlank} icon={<I.download width={16} height={16} />}>Download</AsyncButton>
          </>
        )}
      </div>

      {agreementType && (
        <OptionSheet
          open={pickOpen}
          title={`Fill from an ${agreementType}`}
          onClose={() => setPickOpen(false)}
          searchable
          autoFocusSearch={false}
          searchPlaceholder="Search by reference or address…"
          q={picker.q}
          setQ={picker.setQ}
          onLoadMore={picker.loadMore}
          hasMore={picker.hasMore}
          loadingMore={picker.loadingMore}
          loading={picker.loading && !picker.rows.length}
          notFoundContent={picker.error
            ? `Could not load your agreements — ${picker.error}`
            : picker.q.trim()
              ? `None of your ${AGREEMENTS_NAME[agreementType]} match “${picker.q.trim()}”.`
              : `You have no ${AGREEMENTS_NAME[agreementType]} yet.`}
          options={picker.rows.map((r) => ({
            value: r.id,
            label: [r.form_ref, r.property].filter(Boolean).join(' · '),
            sub: agreementSub(r),
          }))}
          onPick={(o) => { setPickOpen(false); fillFrom(o.value); }}
        />
      )}

      {viewerHtml && <DocViewer title={doc.title} html={viewerHtml} onClose={() => setViewerHtml(null)} />}
      {/* Header reads the document's title; the saved file keeps its real name. */}
      {pdfUrl && (
        <FileViewerLazy
          title={doc.title}
          fileName={doc.file_name || `${doc.title}.pdf`}
          url={pdfUrl}
          onClose={() => { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }}
        />
      )}
    </div>
  );
}

// On-device signature pad — same mechanics as the e-sign pads (mouse + touch).
function SignPad({ onDone }) {
  const { message } = AntApp.useApp();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const posOf = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return undefined;
    const ctx = c.getContext('2d');
    c.width = c.offsetWidth || 300;
    c.height = 160;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const start = (e) => { e.preventDefault(); drawing.current = true; const p = posOf(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = posOf(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
    const end = () => { drawing.current = false; };
    c.addEventListener('touchstart', start, { passive: false });
    c.addEventListener('touchmove', move, { passive: false });
    c.addEventListener('touchend', end);
    return () => {
      c.removeEventListener('touchstart', start);
      c.removeEventListener('touchmove', move);
      c.removeEventListener('touchend', end);
    };
  }, []);

  const mDown = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = posOf(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const mMove = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); const p = posOf(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
  const mUp = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: 160, display: 'block', border: '1.5px dashed var(--border)', borderRadius: 11, background: '#fff', touchAction: 'none', cursor: 'crosshair' }}
        onMouseDown={mDown}
        onMouseMove={mMove}
        onMouseUp={mUp}
        onMouseLeave={mUp}
      />
      <div style={{ display: 'flex', gap: 10 }}>
        <Button size="small" onClick={clear}>Clear</Button>
        <Button
          size="small"
          type="primary"
          style={{ flex: 1 }}
          onClick={() => {
            if (!hasDrawn) { message.warning('Draw the signature first'); return; }
            onDone(canvasRef.current.toDataURL('image/png'));
          }}
        >
          Use this signature
        </Button>
      </div>
    </div>
  );
}
